import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, DragOverlay, rectIntersection, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import SkuListColumn from './SkuListColumn';
import SkuProductsColumn from './SkuProductsColumn';
import ProductSearchColumn from './ProductSearchColumn';
import SkuProductCard from './SkuProductCard';
import SkuEditModal from './SkuEditModal';

interface Product {
  id_catalogo: string;
  title?: string;
  sitio?: string;
  sale_price?: string | number;
  imagen?: string;
  [key: string]: any;
}

interface SkuItem {
  sku: string;
  product_count: number;
  first_product_title: string;
  first_product_image?: string | null;
  custom_name?: string | null;
  custom_image?: string | null;
  reference_price?: number | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface SKUsManagementProps {
  selectedTable: string;
  clientId: string;
}

const SKUsManagement: React.FC<SKUsManagementProps> = ({ selectedTable, clientId }) => {
  const { t } = useTranslation();

  // SKU list state
  const [skuList, setSkuList] = useState<SkuItem[]>([]);
  const [skuSearchTerm, setSkuSearchTerm] = useState('');
  const [isLoadingSkuList, setIsLoadingSkuList] = useState(false);
  const [skuListPagination, setSkuListPagination] = useState<Pagination | undefined>();
  const [skuListPage, setSkuListPage] = useState(1);

  // Selected SKU state
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [skuProducts, setSkuProducts] = useState<Product[]>([]);
  const [isLoadingSkuProducts, setIsLoadingSkuProducts] = useState(false);
  const [skuProductsPagination, setSkuProductsPagination] = useState<Pagination | undefined>();
  const [, setSkuProductsPage] = useState(1);

  // New SKU creation state
  const [isCreatingNewSku, setIsCreatingNewSku] = useState(false);
  const [newSkuCode, setNewSkuCode] = useState('');

  // Product search state
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchPagination, setSearchPagination] = useState<Pagination | undefined>();
  const [, setSearchPage] = useState(1);
  const [lastSearchTerms, setLastSearchTerms] = useState('');
  const [searchResetCounter, setSearchResetCounter] = useState(0);

  // Drag state
  const [activeProduct, setActiveProduct] = useState<Product | null>(null);

  // Edit/Delete modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeletingSku, setIsDeletingSku] = useState(false);

  // Column collapse states
  const [isSearchColumnCollapsed, setIsSearchColumnCollapsed] = useState(false);
  const [isSkuListColumnCollapsed, setIsSkuListColumnCollapsed] = useState(false);

  // Set of product IDs currently in the selected SKU
  const skuProductIds = useMemo(
    () => new Set(skuProducts.map((p) => p.id_catalogo)),
    [skuProducts]
  );

  // Get metadata for selected SKU
  const selectedSkuMetadata = useMemo(() => {
    if (!selectedSku) return null;
    const skuItem = skuList.find((s) => s.sku === selectedSku);
    if (!skuItem) return null;
    return {
      custom_name: skuItem.custom_name,
      custom_image: skuItem.custom_image,
      reference_price: skuItem.reference_price,
      first_product_title: skuItem.first_product_title,
      first_product_image: skuItem.first_product_image,
    };
  }, [selectedSku, skuList]);

  // Load SKU list
  const loadSkuList = useCallback(async (search?: string, page: number = 1) => {
    if (!selectedTable) return;

    setIsLoadingSkuList(true);
    try {
      const params = new URLSearchParams({
        tableName: selectedTable,
        page: String(page),
        limit: '50',
        ...(search && { search }),
      });
      const response = await api.get(`/api/skus/list?${params}`);
      setSkuList(response.data.skus || []);
      setSkuListPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading SKU list:', error);
      setSkuList([]);
      setSkuListPagination(undefined);
    } finally {
      setIsLoadingSkuList(false);
    }
  }, [selectedTable]);

  // Load products for selected SKU
  const loadSkuProducts = useCallback(async (sku: string, page: number = 1) => {
    if (!selectedTable || !sku) return;

    setIsLoadingSkuProducts(true);
    try {
      const params = new URLSearchParams({
        tableName: selectedTable,
        page: String(page),
        limit: '50',
      });
      const response = await api.get(`/api/skus/${encodeURIComponent(sku)}/products?${params}`);
      setSkuProducts(response.data.products || []);
      setSkuProductsPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading SKU products:', error);
      setSkuProducts([]);
      setSkuProductsPagination(undefined);
    } finally {
      setIsLoadingSkuProducts(false);
    }
  }, [selectedTable]);

  // Search products in the current table
  const searchProducts = useCallback(async (terms: string, page: number = 1) => {
    if (!selectedTable || !terms) return;

    setIsSearching(true);
    setLastSearchTerms(terms);
    try {
      // Separar términos por coma - cada término se busca con AND
      const termsArray = terms.split(',').map((t) => t.trim()).filter(Boolean);

      // Crear search_operators para que el backend interprete como AND
      const searchOperators: Record<string, string> = {};
      termsArray.forEach(term => {
        searchOperators[term] = 'AND';
      });

      const params = new URLSearchParams({
        limit: '50',
        offset: String((page - 1) * 50),
        global_search: termsArray.join(' '),
        search_operators: JSON.stringify(searchOperators),
      });
      const response = await api.get(`/api/tables/${selectedTable}/data?${params}`);
      setSearchResults(response.data.data || []);

      // Create pagination from response
      const total = response.data.total || 0;
      setSearchPagination({
        page,
        limit: 50,
        total,
        totalPages: Math.ceil(total / 50)
      });
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults([]);
      setSearchPagination(undefined);
    } finally {
      setIsSearching(false);
    }
  }, [selectedTable]);

  const resetSearch = useCallback(() => {
    setSearchResults([]);
    setSearchPagination(undefined);
    setSearchPage(1);
    setLastSearchTerms('');
    setIsSearching(false);
    setSearchResetCounter((prev) => prev + 1);
  }, []);

  // Add product to SKU
  const addProductToSku = useCallback(async (product: Product) => {
    const targetSku = isCreatingNewSku ? newSkuCode : selectedSku;
    if (!targetSku || !product.id_catalogo) return;

    try {
      await api.post('/api/skus', {
        id_catalogo: product.id_catalogo,
        sku: targetSku,
      });

      // If we were creating a new SKU, select it now
      if (isCreatingNewSku) {
        setIsCreatingNewSku(false);
        setSelectedSku(targetSku);
        setNewSkuCode('');
        // Reload the SKU list to show the new SKU
        loadSkuList(skuSearchTerm, 1);
        setSkuListPage(1);
      }

      // Add product to local state
      setSkuProducts((prev) => [...prev, product]);

      // Remove from search results
      setSearchResults((prev) => prev.filter((p) => p.id_catalogo !== product.id_catalogo));

      // Reload SKU list to update counts
      loadSkuList(skuSearchTerm, skuListPage);
    } catch (error) {
      console.error('Error adding product to SKU:', error);
    }
  }, [selectedSku, isCreatingNewSku, newSkuCode, loadSkuList, skuSearchTerm, skuListPage]);

  // Remove product from SKU
  const removeProductFromSku = useCallback(async (id_catalogo: string) => {
    if (!selectedSku) return;

    try {
      await api.delete(`/api/skus/${encodeURIComponent(id_catalogo)}`);

      // Remove from local state
      setSkuProducts((prev) => prev.filter((p) => p.id_catalogo !== id_catalogo));
      setSkuProductsPagination((prev) => {
        if (!prev) return prev;
        const newTotal = Math.max(0, prev.total - 1);
        return {
          ...prev,
          total: newTotal,
          totalPages: Math.max(1, Math.ceil(newTotal / prev.limit))
        };
      });

      // Reload SKU list to update counts
      loadSkuList(skuSearchTerm, skuListPage);
    } catch (error) {
      console.error('Error removing product from SKU:', error);
    }
  }, [selectedSku, loadSkuList, skuSearchTerm, skuListPage]);

  // Handle SKU selection
  const handleSelectSku = useCallback((sku: string) => {
    setIsCreatingNewSku(false);
    resetSearch();
    setSelectedSku(sku);
    setSkuProductsPage(1);
    loadSkuProducts(sku, 1);
  }, [loadSkuProducts, resetSearch]);

  // Handle create new SKU
  const handleCreateNewSku = useCallback(() => {
    resetSearch();
    setSelectedSku(null);
    setSkuProducts([]);
    setSkuProductsPagination(undefined);
    setIsCreatingNewSku(true);
    setNewSkuCode('');
  }, [resetSearch]);

  // Handle confirm new SKU
  const handleConfirmNewSku = useCallback(() => {
    if (!newSkuCode.trim()) return;
    // Just keep the creation mode active, products will be added by drag & drop
  }, [newSkuCode]);

  // Handle cancel new SKU
  const handleCancelNewSku = useCallback(() => {
    setIsCreatingNewSku(false);
    setNewSkuCode('');
  }, []);

  // Pagination handlers
  const handleSkuListPageChange = useCallback((page: number) => {
    setSkuListPage(page);
    loadSkuList(skuSearchTerm, page);
  }, [loadSkuList, skuSearchTerm]);

  const handleSkuProductsPageChange = useCallback((page: number) => {
    if (!selectedSku) return;
    setSkuProductsPage(page);
    loadSkuProducts(selectedSku, page);
  }, [loadSkuProducts, selectedSku]);

  const handleSearchPageChange = useCallback((page: number) => {
    setSearchPage(page);
    searchProducts(lastSearchTerms, page);
  }, [searchProducts, lastSearchTerms]);

  // Drag handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const product = event.active.data.current?.product as Product;
    if (product) {
      setActiveProduct(product);
    }
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { over, active } = event;
    setActiveProduct(null);

    // Only add if dropped specifically on the drop zone
    if (!over) return;
    if (over.id !== 'sku-products-drop-zone') return;
    if (!active.data.current?.product) return;

    const product = active.data.current.product as Product;
    addProductToSku(product);
  }, [addProductToSku]);

  // Edit SKU handler
  const handleEditSku = useCallback(() => {
    if (selectedSku) {
      setIsEditModalOpen(true);
    }
  }, [selectedSku]);

  // Save SKU metadata handler
  const handleSaveSkuMetadata = useCallback(async (data: {
    new_sku?: string;
    name: string | null;
    image_url: string | null;
    reference_price: number | null;
  }) => {
    if (!selectedSku) return;

    await api.put(`/api/skus/metadata/${encodeURIComponent(selectedSku)}`, data);

    // If SKU was renamed, update selectedSku
    if (data.new_sku && data.new_sku !== selectedSku) {
      setSelectedSku(data.new_sku);
    }

    // Reload SKU list to reflect changes
    loadSkuList(skuSearchTerm, skuListPage);
  }, [selectedSku, loadSkuList, skuSearchTerm, skuListPage]);

  // Delete SKU handler
  const handleDeleteSku = useCallback(() => {
    if (selectedSku) {
      setIsDeleteConfirmOpen(true);
    }
  }, [selectedSku]);

  // Confirm delete SKU handler
  const handleConfirmDeleteSku = useCallback(async () => {
    if (!selectedSku) {
      console.error('No SKU selected for deletion');
      setIsDeleteConfirmOpen(false);
      return;
    }

    // Store SKU to delete before any state changes
    const skuToDelete = selectedSku;

    setIsDeletingSku(true);
    try {
      await api.delete(`/api/skus/group/${encodeURIComponent(skuToDelete)}`);

      // Close modal and clear loading state
      setIsDeleteConfirmOpen(false);
      setIsDeletingSku(false);

      // Clear selection
      setSelectedSku(null);
      setSkuProducts([]);
      setSkuProductsPagination(undefined);

      // Reload the SKU list from server to get fresh data
      // Reset to page 1 to ensure we see the updated list
      setSkuListPage(1);

      // Force reload by directly fetching
      if (selectedTable) {
        setIsLoadingSkuList(true);
        try {
          const params = new URLSearchParams({
            tableName: selectedTable,
            page: '1',
            limit: '50',
            ...(skuSearchTerm && { search: skuSearchTerm }),
          });
          const response = await api.get(`/api/skus/list?${params}`);
          setSkuList(response.data.skus || []);
          setSkuListPagination(response.data.pagination);
        } catch (error) {
          console.error('Error reloading SKU list:', error);
        } finally {
          setIsLoadingSkuList(false);
        }
      }
    } catch (error: any) {
      console.error('Error deleting SKU:', error);
      setIsDeleteConfirmOpen(false);
      setIsDeletingSku(false);
    }
  }, [selectedSku, selectedTable, skuSearchTerm]);

  // Load SKU list on mount and when search term changes
  useEffect(() => {
    setSkuListPage(1);
    const timeoutId = setTimeout(() => {
      loadSkuList(skuSearchTerm, 1);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [skuSearchTerm, loadSkuList]);

  // Clear state when table changes
  useEffect(() => {
    setSelectedSku(null);
    setSkuProducts([]);
    setSkuProductsPagination(undefined);
    setSearchResults([]);
    setSearchPagination(undefined);
    setIsCreatingNewSku(false);
    setNewSkuCode('');
    setSkuListPage(1);
    setSkuProductsPage(1);
    setSearchPage(1);
    setLastSearchTerms('');
  }, [selectedTable]);

  const canAddProducts = Boolean(selectedSku || (isCreatingNewSku && newSkuCode.trim()));

  if (!selectedTable) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center py-12">
          <div className="h-16 w-16 text-gray-400 mx-auto mb-4">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('skus.title')}</h2>
          <p className="text-gray-600">
            {t('skus.select_table')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      collisionDetection={rectIntersection}
    onDragStart={handleDragStart}
    onDragEnd={handleDragEnd}
  >
      <div className="h-[calc(100vh-200px)] min-h-0 bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-12 gap-4 h-full min-h-0">
          {/* Column 1: SKU List - collapsible */}
          <div className={`${isSkuListColumnCollapsed ? 'col-span-1' : 'col-span-3'} h-full min-h-0 transition-all duration-300 relative`}>
            {/* Collapse/Expand Toggle Button */}
            <button
              onClick={() => setIsSkuListColumnCollapsed(!isSkuListColumnCollapsed)}
              className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-1 shadow-md hover:bg-gray-50 transition-colors"
              title={isSkuListColumnCollapsed ? t('skus.expand_sku_list') : t('skus.collapse_sku_list')}
            >
              {isSkuListColumnCollapsed ? (
                <ChevronRight className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronLeft className="h-4 w-4 text-gray-600" />
              )}
            </button>

            {isSkuListColumnCollapsed ? (
              /* Collapsed state - vertical label */
              <div className="h-full bg-white rounded-lg border border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsSkuListColumnCollapsed(false)}
              >
                <div className="writing-mode-vertical text-sm font-medium text-gray-600 transform rotate-180"
                  style={{ writingMode: 'vertical-rl' }}
                >
                  {t('skus.skus_header')}
                </div>
              </div>
            ) : (
              <SkuListColumn
                skuList={skuList}
                selectedSku={selectedSku}
                searchTerm={skuSearchTerm}
                isLoading={isLoadingSkuList}
                pagination={skuListPagination}
                onSearchChange={setSkuSearchTerm}
                onSelectSku={handleSelectSku}
                onCreateNewSku={handleCreateNewSku}
                onPageChange={handleSkuListPageChange}
              />
            )}
          </div>

          {/* Column 2: SKU Products - expands when other columns are collapsed */}
          <div className={`${
            isSkuListColumnCollapsed && isSearchColumnCollapsed ? 'col-span-10' :
            isSkuListColumnCollapsed ? 'col-span-6' :
            isSearchColumnCollapsed ? 'col-span-8' :
            'col-span-4'
          } h-full min-h-0 transition-all duration-300`}>
            <SkuProductsColumn
              selectedSku={selectedSku}
              skuMetadata={selectedSkuMetadata}
              products={skuProducts}
              isLoading={isLoadingSkuProducts}
              isCreatingNewSku={isCreatingNewSku}
              newSkuCode={newSkuCode}
              pagination={skuProductsPagination}
              onNewSkuCodeChange={setNewSkuCode}
              onConfirmNewSku={handleConfirmNewSku}
              onCancelNewSku={handleCancelNewSku}
              onRemoveProduct={removeProductFromSku}
              onPageChange={handleSkuProductsPageChange}
              onEditSku={handleEditSku}
              onDeleteSku={handleDeleteSku}
            />
          </div>

          {/* Column 3: Product Search - collapsible */}
          <div className={`${isSearchColumnCollapsed ? 'col-span-1' : 'col-span-5'} h-full min-h-0 transition-all duration-300 relative`}>
            {/* Collapse/Expand Toggle Button */}
            <button
              onClick={() => setIsSearchColumnCollapsed(!isSearchColumnCollapsed)}
              className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-1 shadow-md hover:bg-gray-50 transition-colors"
              title={isSearchColumnCollapsed ? t('skus.expand_search') : t('skus.collapse_search')}
            >
              {isSearchColumnCollapsed ? (
                <ChevronLeft className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-600" />
              )}
            </button>

            {isSearchColumnCollapsed ? (
              /* Collapsed state - vertical label */
              <div className="h-full bg-white rounded-lg border border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsSearchColumnCollapsed(false)}
              >
                <div className="writing-mode-vertical text-sm font-medium text-gray-600 transform rotate-180"
                  style={{ writingMode: 'vertical-rl' }}
                >
                  {t('skus.search_products')}
                </div>
              </div>
            ) : (
              <ProductSearchColumn
                searchResults={searchResults}
                skuProductIds={skuProductIds}
                isLoading={false}
                isSearching={isSearching}
                canAddProducts={canAddProducts}
                pagination={searchPagination}
                onSearch={searchProducts}
                onAddProduct={addProductToSku}
                onPageChange={handleSearchPageChange}
                onClearSearch={resetSearch}
                resetCounter={searchResetCounter}
              />
            )}
          </div>
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeProduct && (
          <div className="opacity-80">
            <SkuProductCard product={activeProduct} />
          </div>
        )}
      </DragOverlay>

      {/* Edit SKU Modal */}
      {selectedSku && (
        <SkuEditModal
          isOpen={isEditModalOpen}
          sku={selectedSku}
          metadata={selectedSkuMetadata ? {
            sku: selectedSku,
            name: selectedSkuMetadata.custom_name || null,
            image_url: selectedSkuMetadata.custom_image || null,
            reference_price: selectedSkuMetadata.reference_price || null,
            first_product_title: selectedSkuMetadata.first_product_title,
            first_product_image: selectedSkuMetadata.first_product_image,
          } : null}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveSkuMetadata}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !isDeletingSku && setIsDeleteConfirmOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {t('skus.delete_confirm.title')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('skus.delete_confirm.message', { sku: selectedSkuMetadata?.custom_name || selectedSku })}
            </p>
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg mb-4">
              {t('skus.delete_confirm.warning')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteConfirmOpen(false)}
                disabled={isDeletingSku}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('skus.delete_confirm.cancel')}
              </button>
              <button
                onClick={handleConfirmDeleteSku}
                disabled={isDeletingSku}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeletingSku && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                )}
                {t('skus.delete_confirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DndContext>
  );
};

export default SKUsManagement;
